import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { first, Subject, takeUntil } from 'rxjs';
import { INivelesPromo, IProductos, IPromosEcommerce, T_TipoPromocion } from '../../../private/shared/models/productos';
import { TransferDataService } from '../../../private/shared/services/transfer-data.service';
import { addCartPromoState } from '../../../store/actions';
import { AppState } from '../../../store/app-reducers';
import { EcommerceAnalyticsService } from '../../analytics/ecommerce-analytics/ecommerce-analytics.service';
import { SharedAnalyticsService } from '../../analytics/shared-analytics/shared-analytics.service';
import { EAnalyticsPageViews } from '../../enums/analytics.enum';
import { IAnalyticsListContext } from '../../models/Analytics';
import { SECTIONS } from '../../models/Constants';
import { ModalsService } from '../../services/modals/modals.service';
import { OperationsService } from '../../services/operations/operations.service';
import { PromoInfoModalPage } from '../promo-info-modal/promo-info-modal.page';
import { PromoSummaryModalPage } from '../promo-summary-modal/promo-summary-modal.page';
import { CartProductsState } from './../../../store/reducers';

@Component({
  selector: 'getm-ladder-promo',
  templateUrl: './ladder-promo.page.html',
  styleUrls: ['./ladder-promo.page.scss'],
})
export class LadderPromoPage implements OnInit, OnDestroy {

  @Input() promo: IPromosEcommerce = {} as IPromosEcommerce;
  @Input() analyticsContext?: IAnalyticsListContext;

  private finishSubscriptions: Subject<boolean> = new Subject();
  private previousCartPromoProducts: IProductos[] = []; // Para detectar cambios en composición de productos

  public productosPromocion: IProductos[] = [];
  public nivelesPromo: INivelesPromo[] = [];
  public level = 1;
  public cartPromoProducts: IProductos[] = [];
  public canConfirm = false;
  public showSummary = false;
  public disabledProducts = false;
  public isLoading = true;
  public readonly isLastLevel = true;
  public readonly isEditing = false;
  public promoDetail = {
    title: '',
    available: 0,
    ends: '',
    select: 0,
    max: 0,
    tipoPromo: 'ESCALERA' as T_TipoPromocion,
  };

  constructor(
    private store: Store<AppState>
    , private modalS: ModalsService
    , private modalCtrl: ModalController
    , private transferDataS: TransferDataService
    , private operationsS: OperationsService
    , private ecommerceAnalyticsS: EcommerceAnalyticsService
    , private sharedAnalyticsS: SharedAnalyticsService
  ) { }

  ngOnInit() {
    // Disparar evento page_view para el modal de promoción escalera
    this.sharedAnalyticsS.trackPageViewModal(EAnalyticsPageViews.promo_escalera_modal, this.analyticsContext?.section || SECTIONS.TIENDA);

    if (!this.promo) {
      this.initializePromoDetail();
      this.isLoading = false;
      return;
    }

    this.nivelesPromo = this.promo.Niveles || [];
    this.cartPromoProducts = (this.promo.Productos || []).map(p => ({ ...p, productos: 0 }));
    this.productosPromocion = this.promo.Productos || [];

    this.promoDetail = {
      title: this.promo.Promocion || '',
      available: this.promo?.MaximoPaquetes - this.promo?.PaquetesCanjeados,
      ends: this.promo.Vigencia?.toString() || '',
      select: 0,
      max: this.nivelesPromo.find(n => n.Nivel === this.level)?.Maximo ?? 0,
      tipoPromo: 'ESCALERA',
    };

    this.operationsS.combinerCartOrder$().pipe(
      first(),
      takeUntil(this.finishSubscriptions)
    ).subscribe(this.callBackInfoPromo);
  }

  ngOnDestroy() {
    this.finishSubscriptions.next(true);
    this.finishSubscriptions.complete();
  }

  private callBackInfoPromo = (cart: CartProductsState) => {
    const cartPromos = cart.cartPromos.filter(p => p.ClavePromo === this.promo.ClavePromo);
    const exchange = cart.promo.find(p => p.ClavePromo === this.promo.ClavePromo)?.PaquetesCanjeados || 1;
    if (cartPromos.length > 0) {
      this.cartPromoProducts = this.cartPromoProducts.map(pp => {
        const found = cartPromos.find(x => x.clave_producto === pp.clave_producto);
        return found ? { ...pp, productos: (found.productos || 0) / exchange, Nivel: found.Nivel } : pp;
      });
    }

    const totalSelected = this.transferDataS.sumTotal(this.cartPromoProducts, 'productos');
    const activeNivel = this.nivelesPromo.find(n => totalSelected >= n.Minimo && totalSelected <= n.Maximo);
    this.level = activeNivel?.Nivel ?? 1;

    this.cartPromoProducts = this.cartPromoProducts.map(p => ({
      ...p,
      Nivel: this.level,
      precio: p.precio_original ?? p.precio ?? 0,
    }));

    // Guardar el estado anterior de los productos para detectar cambios
    this.previousCartPromoProducts = JSON.parse(JSON.stringify(this.cartPromoProducts));
    this.isLoading = false;
    this.updateState();
  }

  public onQuantityChange(updatedProducts: IProductos[]) {
    updatedProducts.forEach(updated => {
      const idx = this.cartPromoProducts.findIndex(p => p.clave_producto === updated.clave_producto);
      if (idx >= 0) {
        this.cartPromoProducts[idx] = {
          ...this.cartPromoProducts[idx],
          productos: updated.productos,
          precio: this.cartPromoProducts[idx].precio_original ?? this.cartPromoProducts[idx].precio ?? 0,
        };
      }
    });

    const totalSelected = this.transferDataS.sumTotal(this.cartPromoProducts, 'productos');
    const activeNivel = this.nivelesPromo.find(n => totalSelected >= n.Minimo && totalSelected <= n.Maximo);
    this.level = activeNivel?.Nivel ?? 1;

    this.updateState();
  }

  /** Cierra el modal sin confirmar */
  public onBack = () => {
    this.modalCtrl.dismiss(false);
  }

  /** Abre el modal de información de la promo */
  public onInfo = async () => {
    if (!this.promo) return;
    await this.modalS.presentModalPromoInfo(PromoInfoModalPage, { promo: this.promo });
  }

  /** Abre el resumen de la promo (botón de resumen) */
  public onSummary = async () => {
    if (!this.promo) return;
    await this.openPromoSummary(true);
  }

  /** Siempre abre el resumen para confirmar */
  public onAction = async () => {
    await this.openPromoSummary(false);
  }

  /** Actualiza el estado completo del componente */
  private updateState() {
    if (!this.promo) return;

    const totalSelected = this.transferDataS.sumTotal(this.cartPromoProducts, 'productos');

    const nivel1Minimo = this.nivelesPromo.find(n => n.Nivel === 1)?.Minimo ?? 0;
    this.canConfirm = totalSelected >= nivel1Minimo && nivel1Minimo > 0;

    this.showSummary = totalSelected > 0;

    const currentLevelMaximo = this.nivelesPromo.find(n => n.Nivel === this.level)?.Maximo ?? 0;
    this.promoDetail = {
      ...this.promoDetail,
      select: totalSelected,
      max: currentLevelMaximo,
    };

    this.updateLevels(totalSelected);
    this.disabledProducts = this.calculateDisabledProducts(totalSelected);
    this.productosPromocion = this.cartPromoProducts.slice();
  }

  /** Abre el resumen de la promo para confirmar */
  private openPromoSummary = async (isResume: boolean) => {
    const productsWithDiscount = this.applyLevelDiscount(this.cartPromoProducts);

    const data = await this.modalS.presentModalPromoSummary(PromoSummaryModalPage, {
      promo: this.promo,
      productosPromocion: productsWithDiscount,
      isResume,
    });

    if (typeof data === 'number') {
      await this.confirmPromoWithQuantity(data);
    }
  }

  /** Aplica el descuento del nivel actual a los productos */
  private applyLevelDiscount(products: IProductos[]): IProductos[] {
    const currentLevel = this.nivelesPromo.find(n => n.Nivel === this.level);
    if (!currentLevel) {
      return products;
    }

    return products.map(p => ({
      ...p,
      Nivel: currentLevel.Nivel,
      precio: currentLevel.Descuento
        ? (p.precio_original ?? p.precio ?? 0) * (1 - currentLevel.Descuento)
        : (p.precio_original ?? p.precio ?? 0),
    }));
  }

  /** Agrega la promo al store desde el modal de resumen con la cantidad especificada */
  private confirmPromoWithQuantity = async (quantity: number) => {
    const previousQuantity = this.promo.PaquetesCanjeados || 0;

    const cartPromosWithLevel = this.applyLevelDiscount(this.cartPromoProducts);
    this.store.dispatch(addCartPromoState({
      promo: this.promo,
      cartPromos: cartPromosWithLevel,
      promoSelect: quantity,
    }));

    const addedRaw: IProductos[] = [];
    const removedRaw: IProductos[] = [];

    this.cartPromoProducts.forEach(current => {
      const previous = this.previousCartPromoProducts.find(
        p => p.clave_producto === current.clave_producto
      );
      const previousQty = previous ? (previous.productos || 0) * previousQuantity : 0;
      const currentQty = (current.productos || 0) * quantity;
      const delta = currentQty - previousQty;

      if (delta > 0) {
        addedRaw.push({ ...current, productos: delta });
      } else if (delta < 0) {
        removedRaw.push({ ...current, productos: Math.abs(delta) });
      }
    });

    const addedItems = this.applyLevelDiscount(addedRaw);
    const removedItems = this.applyLevelDiscount(removedRaw);

    const { listId, listName, section } = this.ecommerceAnalyticsS.resolvePromoAnalyticsContext(
      this.analyticsContext,
      'ESCALERA'
    );

    if (addedItems.length > 0) {
      this.ecommerceAnalyticsS.sendAddToCart({
        products: addedItems,
        value: (addedItems.reduce((sum, p) => sum + ((p.precio || 0) * (p.productos ?? 0)), 0)),
        section,
        listId,
        listName,
        promoMap: this.ecommerceAnalyticsS.buildPromoImpactMap([this.promo])
      });
    }

    if (removedItems.length > 0) {
      this.ecommerceAnalyticsS.sendRemoveFromCart({
        products: removedItems,
        value: (removedItems.reduce((sum, p) => sum + ((p.precio || 0) * (p.productos ?? 0)), 0)),
        section,
        listId,
        listName,
        promoMap: this.ecommerceAnalyticsS.buildPromoImpactMap([this.promo])
      });
    }

    this.modalCtrl.dismiss(true);
  }



  /** Inicializa promoDetail cuando no hay promo */
  private initializePromoDetail() {
    this.nivelesPromo = [];
    this.cartPromoProducts = [];
    this.productosPromocion = [];
    this.promoDetail = {
      title: '',
      available: 0,
      ends: '',
      select: 0,
      max: 0,
      tipoPromo: 'ESCALERA',
    };
  }

  /** Actualiza niveles con estado Cumple basado en total seleccionado */
  private updateLevels(totalSelected: number): void {
    this.nivelesPromo = this.nivelesPromo.map(n => ({
      ...n,
      Cumple: totalSelected >= n.Minimo,
    }));
  }

  /** Calcula si los productos están deshabilitados */
  private calculateDisabledProducts(totalSelected: number): boolean {
    const lastLevelMaximo = this.nivelesPromo.length > 0
      ? (this.nivelesPromo[this.nivelesPromo.length - 1]?.Maximo ?? 0)
      : 0;
    return lastLevelMaximo > 0 && totalSelected >= lastLevelMaximo;
  }
}
